import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../../config/api';
import { formatCurrency, calculateServiceCharge } from '../../utils/calculations';

const OrderEditSection = ({ order, onOrderUpdate, onClose }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [editedItems, setEditedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (order) {
      setEditedItems([...(order.items || [])]);
      fetchMenuItems();
    }
  }, [order]);

  const fetchMenuItems = async () => {
    if (!order?.restaurantId) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.CUSTOMER_MENU}?restaurantId=${order.restaurantId}`);
      const data = await response.json();
      setMenuItems(data.filter(item => item.isAvailable));
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      setEditedItems(editedItems.filter(item => (item.itemId || item._id) !== itemId));
    } else {
      setEditedItems(editedItems.map(item => 
        (item.itemId || item._id) === itemId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const addMenuItem = (menuItem) => {
    const existingItem = editedItems.find(item => (item.itemId || item._id) === menuItem._id);
    if (existingItem) {
      updateQuantity(menuItem._id, existingItem.quantity + 1);
    } else {
      setEditedItems([...editedItems, {
        itemId: menuItem._id,
        name: menuItem.itemName,
        quantity: 1,
        price: menuItem.itemPrice
      }]);
    }
  };

  const calculateTotals = () => {
    const subtotal = editedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const taxAmount = subtotal * ((order?.taxPercentage || 18) / 100);
    // Calculate service charge based on subtotal using the utility function
    const serviceAmount = calculateServiceCharge(subtotal);
    const finalAmount = subtotal + taxAmount + serviceAmount - ((order?.offerDiscount || 0));
    return { subtotal, taxAmount, serviceAmount, finalAmount };
  };

  const handleSaveOrder = async () => {
    setLoading(true);
    setSuccessMessage('');
    try {
      const { subtotal, taxAmount, serviceAmount, finalAmount } = calculateTotals();
      
      const updateData = {
        items: editedItems.map(item => ({
          itemId: item.itemId || item._id,
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        totalAmount: subtotal,
        taxAmount,
        serviceAmount,
        finalAmount,
        editedBy: 'customer'
      };

      const response = await fetch(API_ENDPOINTS.ORDER_BY_ID(order._id), {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedOrder = await response.json();
        setSuccessMessage('Order updated successfully!');
        setTimeout(() => {
          onOrderUpdate(updatedOrder);
          onClose();
          setSuccessMessage('');
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating order:', error);
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, taxAmount, serviceAmount, finalAmount } = calculateTotals();
  const filteredMenuItems = menuItems.filter(item => 
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
          <span>✏️</span>
          <span>Edit Order</span>
        </h2>
        <motion.button 
          onClick={onClose} 
          className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-600 text-xl"
          whileTap={{ scale: 0.9 }}
        >
          ×
        </motion.button>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Current Items */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-3">
            <span className="text-lg sm:text-xl">🛒</span>
            <h3 className="text-base sm:text-lg font-bold text-gray-800">Your Cart ({editedItems.length})</h3>
          </div>
          <div className="max-h-48 sm:max-h-64 overflow-y-auto space-y-2 sm:space-y-3">
            {editedItems.length === 0 ? (
              <div className="text-center py-8">
                <span className="text-4xl mb-2 block">🛒</span>
                <p className="text-gray-500">Cart is empty</p>
              </div>
            ) : (
              editedItems.map((item, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm border border-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-400 rounded-lg flex items-center justify-center">
                      <span className="text-white">🍽️</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{item.name}</h4>
                      <p className="text-sm text-gray-600">{formatCurrency(item.price)} each</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <motion.button
                      onClick={() => updateQuantity(item.itemId || item._id, item.quantity - 1)}
                      className="w-4 md:w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                      whileTap={{ scale: 0.9 }}
                    >
                      -
                    </motion.button>
                    <span className="w-8 text-center font-bold">{item.quantity}</span>
                    <motion.button
                      onClick={() => updateQuantity(item.itemId || item._id, item.quantity + 1)}
                      className="w-4 md:w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center"
                      whileTap={{ scale: 0.9 }}
                    >
                      +
                    </motion.button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Add Items */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3 sm:p-4">
          <div className="flex items-center space-x-2 mb-3">
            <span className="text-lg sm:text-xl">🍽️</span>
            <h3 className="text-base sm:text-lg font-bold text-gray-800">Add Items</h3>
          </div>
          <div className="mb-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 pl-8 sm:pl-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
              />
              <span className="absolute left-2.5 sm:left-3 top-2.5 sm:top-3.5 text-gray-400 text-sm">🔍</span>
            </div>
          </div>
          <div className="max-h-48 sm:max-h-64 overflow-y-auto space-y-2 sm:space-y-3">
            {filteredMenuItems.length === 0 ? (
              <div className="text-center py-8">
                <span className="text-4xl mb-2 block">🔍</span>
                <p className="text-gray-500">
                  {searchTerm ? 'No items found' : 'No items available'}
                </p>
              </div>
            ) : (
              filteredMenuItems.map((item, index) => (
                <motion.div 
                  key={item._id} 
                  className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center">
                      <span className="text-white">🍽️</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{item.itemName}</h4>
                      <p className="text-sm text-gray-600">{formatCurrency(item.itemPrice)}</p>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => addMenuItem(item)}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg font-medium"
                    whileTap={{ scale: 0.95 }}
                  >
                    Add
                  </motion.button>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-xl">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4">
          <div className="text-center bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 rounded-xl">
            <p className="text-sm opacity-90">Subtotal</p>
            <p className="font-bold">{formatCurrency(subtotal)}</p>
          </div>
          <div className="text-center bg-gradient-to-br from-purple-500 to-purple-600 text-white p-3 rounded-xl">
            <p className="text-sm opacity-90">Tax</p>
            <p className="font-bold">{formatCurrency(taxAmount)}</p>
          </div>
          <div className="text-center bg-gradient-to-br from-orange-500 to-orange-600 text-white p-3 rounded-xl">
            <p className="text-sm opacity-90">Service</p>
            <p className="font-bold">{formatCurrency(serviceAmount)}</p>
          </div>
          <div className="text-center bg-gradient-to-br from-green-500 to-green-600 text-white p-3 rounded-xl">
            <p className="text-sm opacity-90">Total</p>
            <p className="font-bold text-lg">{formatCurrency(finalAmount)}</p>
          </div>
        </div>
        
        {successMessage && (
          <motion.div 
            className="mb-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded-xl text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <span className="mr-2">✅</span>
            {successMessage}
          </motion.div>
        )}
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <motion.button
            onClick={onClose}
            className="w-full sm:flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold"
            whileTap={{ scale: 0.98 }}
          >
            Cancel
          </motion.button>
          <motion.button
            onClick={handleSaveOrder}
            disabled={loading}
            className="w-full sm:flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-semibold disabled:bg-gray-400"
            whileTap={{ scale: loading ? 1 : 0.98 }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default OrderEditSection;